import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Trash2, Loader2, Edit2, X } from 'lucide-react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Industry() {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newIndustry, setNewIndustry] = useState({ name: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '', description: '' });
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const token = Cookies.get('admin_token');
      const response = await axios.get(`${API}/master/industries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIndustries(response.data.data || []);
    } catch (err) {
      toast.error('Failed to load industries');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newIndustry.name.trim()) return;

    setAdding(true);
    try {
      const token = Cookies.get('admin_token');
      await axios.post(`${API}/master/industries`, newIndustry, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Industry added successfully');
      setNewIndustry({ name: '', description: '' });
      setOpen(false);
      fetchIndustries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add industry');
    } finally {
      setAdding(false);
    }
  };

  const handleEdit = (industry) => {
    setEditingId(industry.id);
    setEditData({ name: industry.name, description: industry.description || '' });
  };

  const handleUpdate = async (id) => {
    if (!editData.name.trim()) return;

    setUpdating(true);
    try {
      const token = Cookies.get('admin_token');
      await axios.put(`${API}/master/industries/${id}`, editData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Industry updated successfully');
      setEditingId(null);
      fetchIndustries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update industry');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this industry?')) return;

    try {
      const token = Cookies.get('admin_token');
      await axios.delete(`${API}/master/industries/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Industry deleted');
      fetchIndustries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete industry');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading mb-2">Industry</h1>
          <p className="text-muted-foreground">Manage industries in the system</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="editorial">
              <Plus className="h-4 w-4 mr-2" /> Add Industry
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Industry</DialogTitle>
              <DialogDescription>Enter industry name and optional description</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Industry Name *</Label>
                  <Input
                    value={newIndustry.name}
                    onChange={(e) => setNewIndustry({...newIndustry, name: e.target.value})}
                    placeholder="Enter industry name"
                    disabled={adding}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={newIndustry.description}
                    onChange={(e) => setNewIndustry({...newIndustry, description: e.target.value})}
                    placeholder="Enter description (optional)"
                    disabled={adding}
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={adding}>
                  Cancel
                </Button>
                <Button type="submit" disabled={adding || !newIndustry.name.trim()}>
                  {adding ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</>
                  ) : (
                    'Add Industry'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Industries List */}
      <Card>
        <CardHeader>
          <CardTitle>All Industries ({industries.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : industries.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No industries added yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {industries.map((industry) => (
                <div
                  key={industry.id}
                  className="p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  {editingId === industry.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Industry Name</Label>
                        <Input
                          value={editData.name}
                          onChange={(e) => setEditData({...editData, name: e.target.value})}
                          disabled={updating}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Textarea
                          value={editData.description}
                          onChange={(e) => setEditData({...editData, description: e.target.value})}
                          disabled={updating}
                          rows={2}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(industry.id)}
                          disabled={updating || !editData.name.trim()}
                        >
                          {updating ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            'Save'
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingId(null)}
                          disabled={updating}
                        >
                          <X className="h-4 w-4 mr-2" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium mb-1">{industry.name}</h3>
                        {industry.description && (
                          <p className="text-sm text-muted-foreground">{industry.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(industry)}
                        >
                          <Edit2 className="h-4 w-4 text-primary" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(industry.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
